import { Link } from 'react-router-dom';
import { ArrowRight, Feather, Clock, Calendar } from 'lucide-react';
import { FIELD_NOTES } from '../data/field-notes';
import SEO from '../components/SEO';

const HERO_IMG =
  'https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/jm65j708_Global-XRS-2010-Interior-Galley-scaled.jpg';

const InsightsPage = () => {
  const hasArticles = FIELD_NOTES.length > 0;

  return (
    <div data-testid="insights-page" className="min-h-screen">
      <SEO
        title="Insights — Field Notes on private aviation"
        description="First-person editorial articles from Nicolas: the operational reality of business aviation, the mistakes candidates make, and what recruiters actually look for."
        path="/insights"
      />
      {/* ===== HERO WITH IMAGE ===== */}
      <section
        data-testid="insights-hero"
        className="relative min-h-[60vh] md:min-h-[65vh] flex items-center"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMG}
            alt="Gate 33 Insights — first-person field notes from private aviation"
            title="Gate 33 Insights — real knowledge, from real flights"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/85 to-[#FDFBF7]/40" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 py-24 w-full text-center">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Feather size={16} className="text-[#C5A880]" strokeWidth={1.5} />
              <span className="overline">Field Notes · Lived experience</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#0F0F0F] mb-6">
              Real knowledge, from real flights.
            </h1>
            <p className="text-lg text-[#1A1A1A] leading-relaxed mx-auto max-w-xl">
              First-person articles by Nicolas — what actually happens when you apply,
              when you fly, and when you sit on the other side of the interview table.
              No theory. No fluff.
            </p>
          </div>
        </div>
      </section>

      {/* ===== ARTICLES ===== */}
      {hasArticles ? (
        <section data-testid="insights-articles" className="section-padding bg-white">
          <div className="container-custom max-w-6xl">
            <div className="mb-10 flex items-baseline justify-between flex-wrap gap-3">
              <span className="overline">Latest</span>
              <span className="text-xs uppercase tracking-[0.2em] text-[#9A9A9A]">
                {FIELD_NOTES.length} {FIELD_NOTES.length > 1 ? 'articles' : 'article'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {FIELD_NOTES.map((note) => (
                <Link
                  key={note.slug}
                  to={`/insights/field-notes/${note.slug}`}
                  data-testid={`field-note-${note.slug}`}
                  className="group flex flex-col border border-[#E5E0D8] overflow-hidden hover:border-[#C5A880] transition-all duration-300 bg-white"
                >
                  {note.hero && (
                    <div className="aspect-[16/9] overflow-hidden bg-[#FDFBF7]">
                      <img
                        src={note.hero}
                        alt={note.heroAlt || note.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="p-8 flex flex-col flex-grow">
                    <div className="flex items-center gap-4 mb-4 text-[10px] uppercase tracking-[0.2em] text-[#5A5A5A]">
                      <span className="text-[#C5A880] font-semibold">{note.category}</span>
                      <span className="flex items-center gap-1.5"><Calendar size={11} /> {note.date}</span>
                      <span className="flex items-center gap-1.5"><Clock size={11} /> {note.readTime}</span>
                    </div>
                    <h2 className="font-heading text-2xl leading-tight text-[#0F0F0F] mb-4 group-hover:text-[#C5A880] transition-colors">
                      {note.title}
                    </h2>
                    <p className="text-sm text-[#5A5A5A] leading-relaxed line-clamp-4 mb-6 flex-grow">
                      {note.dek}
                    </p>
                    <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0F0F0F] font-semibold group-hover:text-[#C5A880] transition-colors">
                      Read the article <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section data-testid="insights-empty" className="section-padding bg-white">
          <div className="container-custom max-w-2xl text-center">
            <span className="overline inline-block mb-4">Coming soon</span>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#0F0F0F] mb-6">
              New articles are being written.
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed">
              Field notes from Nicolas — coming shortly. Check back soon.
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

export default InsightsPage;
